// 0.41.78 - Reading values out of an API response.
// 0.47.74 - Filter operators (equals, does not equal, number comparisons).
import { cascadeMatches, cascadeOp } from './cascadeMatch';
import { readPath, apiRows } from './apiResponsePath';
/**
 * resolveElementOptions — shared source of truth for the {value, label}
 * list a dropdown / multiselect / radio / checkbox displays.
 *
 * 0.37.54 — Extracted from PageContentRenderer.jsx (where it lived as
 * `_resolveElementOptions` since 0.36.278). Two consumers:
 *
 *   1. Renderers (PageContentRenderer, AdminFormPreview) — the
 *      original use: build the option list the citizen sees.
 *
 *   2. The rules engine's labels_of() expression function — was
 *      re-implementing option resolution and drifting from the
 *      renderer's logic (missing filter/exclude support, wrong
 *      priority when el.library AND el.options both present).
 *      Now calls THIS resolver so labels_of() returns the exact
 *      labels the dropdown shows.
 *
 * Purity: no side effects. Callers who need to trigger a library
 * fetch on cache miss (labels_of does; renderers rely on their
 * own prefetch effect) do so BEFORE calling this function, then
 * pass the resulting librariesData in.
 *
 * ctx contract:
 *   librariesData?: { [libKey]: { rows: object[], pk: string } }
 *   values?:        { [fieldName]: any }        // for cascade filter
 *   ruleState?: {                                // optional — passing
 *     addedOptions?: Map<name, {value,label}[]>, //  null skips rule
 *     sortOrder?: Map<name, 'asc'|'desc'|'none'> //  overlays (see
 *   }                                            //  labels_of caveat)
 *
 * Missing ctx / ruleState / librariesData is safe — the function
 * returns whatever it can build with what it has. For a
 * library-backed element with no librariesData, that's `[]`.
 *
 * Steps applied in order:
 *   1. Source options — library (with cascade filter + exclude_values)
 *      OR manual (el.options).
 *   2. Static extras — el.extra_options.
 *   3. Rule-added options — from add_option effect, via ruleState.
 *   4. Sort order — rule set_sort_order overrides el.sort overrides 'none'.
 *   5. Compose per el.extras_position ('top' | 'bottom' | 'sorted').
 */
export function resolveElementOptions(e, ctx) {
  if (!e) return [];
  const name = e.name;

  // --- 1. Source options ---
  let baseOptions = [];
  const source = e.source || 'manual';
  if (source === 'library') {
    const libData = ctx && ctx.librariesData && ctx.librariesData[e.library];
    if (libData && Array.isArray(libData.rows)) {
      // 0.36.626 — Cascading filter. If this element declares
      // filter_by_element + filter_by_column, filter the raw rows
      // by another element's current value. Empty parent value ⇒
      // hide options entirely (per user spec).
      // 0.36.658 — Parent may be a multiselect. When parentVal is
      // an array, match rows whose filter_by_column equals ANY of
      // the selected parent values (OR / union). Single-value
      // parent behavior unchanged.
      let rows = libData.rows;
      if (e.filter_by_element && e.filter_by_column) {
        const parentVal = ctx && ctx.values
          ? ctx.values[e.filter_by_element] : '';
        if (parentVal === '' || parentVal == null
            || (Array.isArray(parentVal) && parentVal.length === 0)) {
          rows = [];
        } else {
          const targets = new Set(
            Array.isArray(parentVal)
              ? parentVal.map(v => String(v))
              : [String(parentVal)]
          );
          rows = rows.filter(r => cascadeMatches(r[e.filter_by_column], parentVal, cascadeOp(e)));
        }
      }
      // 0.38.36 - Include values. Static list of library-derived
      // values to keep for this dropdown - everything not in the
      // list gets dropped. Applied AFTER cascade filter, BEFORE
      // exclude and BEFORE label build.
      //
      // 0.38.37 - Semantics tightened for consistency with admin's
      // visual model in the LibraryRowSelector:
      //
      //   include_values missing (undefined)  -> include ALL rows
      //                                          (legacy / no filter)
      //   include_values = [] (empty)         -> include NO rows
      //                                          (admin has explicitly
      //                                          cleared their selection)
      //   include_values = ['a', 'b']         -> include just those
      //
      // The empty-array case previously matched the missing case
      // (both meant "no filter"), which broke the master checkbox:
      // clicking to uncheck all didn't visibly clear the row checkboxes
      // because runtime still included every row. Now empty array
      // is honored as an explicit empty selection - matches what
      // admin sees.
      //
      // Backwards compat: no existing element carries an empty
      // include_values array (the field was introduced in 0.38.36
      // and its parent always normalized empty to undefined until
      // this ship), so no data flips meaning on upgrade.
      /* 0.49.50 - include_rows: the rows by the library's own key, so
         rows sharing a value (Months and days: Number 1 for Day,
         January and Monday) are told apart. include_values (values) is
         the older form, still read. */
      if (Array.isArray(e.include_rows)) {
        const keySet = new Set(e.include_rows.map(v => String(v)));
        rows = rows.filter(r => keySet.has(String(r[libData.pk])));
      } else if (Array.isArray(e.include_values)) {
        const valueField = e.value_column || libData.pk;
        const includeSet = new Set(e.include_values.map(v => String(v)));
        rows = rows.filter(r => includeSet.has(String(r[valueField])));
      }
      // 0.36.646 — Exclude values. Static list of library-derived
      // values that should be hidden from this dropdown. Applied
      // after any cascade filter, before labels are built. Empty
      // list (or absent) → no exclusions.
      if (Array.isArray(e.exclude_values) && e.exclude_values.length > 0) {
        const valueField = e.value_column || libData.pk;
        const excludeSet = new Set(e.exclude_values.map(v => String(v)));
        rows = rows.filter(r => !excludeSet.has(String(r[valueField])));
      }
      const valueField = e.value_column || libData.pk;
      // 0.36.623 — Multi-column label joining. `e.label_columns`
      // (string[]) takes precedence over the legacy `e.label_column`.
      // Empty / whitespace-only values dropped so a missing
      // translation doesn't leave a dangling separator.
      const cols = Array.isArray(e.label_columns) && e.label_columns.length > 0
        ? e.label_columns
        : (e.label_column ? [e.label_column] : []);
      const sep = typeof e.label_separator === 'string' ? e.label_separator : ' ';
      if (valueField && cols.length > 0) {
        baseOptions = rows.map(row => {
          const parts = cols
            .map(c => (row[c] == null ? '' : String(row[c])))
            .filter(s => s.trim() !== '');
          return {
            value: String(row[valueField] ?? ''),
            label: parts.join(sep),
          };
        });
      }
    }
  } else if (source === 'api') {
    /* 0.41.78 - Options from an API response.
     *
     * Reads whatever a rule has already fetched, exactly as an
     * API-backed TABLE does: the same stored response, the same
     * "empty until something calls it" behaviour. A library-backed
     * dropdown fetches for itself; this one deliberately does not,
     * because a call that fires on mount needs a trigger the platform
     * does not have yet, and inventing a second fetching path here
     * would have to be unpicked when it arrives.
     *
     * So both an API table and an API dropdown fill at the same
     * moment, from the same call.
     */
    const entry = (ctx && ctx.apiState)
      ? ctx.apiState[(e.api_connection || '').trim()] : null;
    if (entry && entry.response !== undefined) {
      let rows = apiRows(entry.response, e.api_rows_path || '');

      // Filter by another field, with the rules the server uses for a
      // library: exact, case-sensitive, comma-separated value matching
      // any of its parts.
      if (e.filter_by_element && e.filter_by_column) {
        const parentVal = ctx && ctx.values ? ctx.values[e.filter_by_element] : '';
        const parts = (Array.isArray(parentVal) ? parentVal : [parentVal])
          .flatMap(v => String(v ?? '').split(','))
          .map(v => v.trim())
          .filter(Boolean);
        if (parts.length === 0) {
          // An unset parent shows nothing, as a cascading library
          // dropdown does - otherwise the citizen picks from every
          // row before answering the question that narrows them.
          rows = [];
        } else {
          rows = rows.filter(r =>
            cascadeMatches(readPath(r, e.filter_by_column) ?? '', parts, cascadeOp(e)));
        }
      }

      const valueField = e.value_column || '';
      const cols = Array.isArray(e.label_columns) && e.label_columns.length > 0
        ? e.label_columns
        : (e.label_column ? [e.label_column] : []);
      const sep = typeof e.label_separator === 'string' ? e.label_separator : ' ';
      if (valueField && cols.length > 0) {
        baseOptions = rows.map(row => {
          const parts = cols
            .map(c => {
              const v = readPath(row, c);
              return v == null ? '' : String(v);
            })
            .filter(s => s.trim() !== '');
          return {
            value: String(readPath(row, valueField) ?? ''),
            label: parts.join(sep),
          };
        });
      }
    }
  } else {
    baseOptions = Array.isArray(e.options) ? e.options : [];
  }

  // --- 2. Static extras from element JSON ---
  const staticExtras = Array.isArray(e.extra_options) ? e.extra_options : [];

  // --- 3. Rule-added options (from add_option effect) ---
  let ruleAdded = [];
  if (name && ctx && ctx.ruleState && ctx.ruleState.addedOptions) {
    const arr = ctx.ruleState.addedOptions.get(name);
    if (Array.isArray(arr)) ruleAdded = arr;
  }

  // --- 4. Sort order (rule override > element static > 'none') ---
  let sortOrder = e.sort || 'none';
  if (name && ctx && ctx.ruleState && ctx.ruleState.sortOrder) {
    const ruleSort = ctx.ruleState.sortOrder.get(name);
    if (ruleSort === 'asc' || ruleSort === 'desc' || ruleSort === 'none') {
      sortOrder = ruleSort;
    }
  }
  const sortFn = (a, b) => {
    const cmp = String(a.label ?? '').localeCompare(
      String(b.label ?? ''), undefined, { sensitivity: 'base' });
    return sortOrder === 'asc' ? cmp : -cmp;
  };
  const applySort = (arr) => (sortOrder === 'asc' || sortOrder === 'desc')
    ? [...arr].sort(sortFn) : arr;

  // --- 5. Compose per extras_position ---
  const position = e.extras_position || 'bottom';
  const extrasBlock = [...staticExtras, ...ruleAdded];
  if (position === 'sorted') {
    return applySort([...baseOptions, ...extrasBlock]);
  }
  // 'top' or 'bottom' — sort the source portion only; extras keep
  // their admin-defined order in their fixed slot.
  const sortedBase = applySort(baseOptions);
  return position === 'top'
    ? [...extrasBlock, ...sortedBase]
    : [...sortedBase, ...extrasBlock];
}
